import React, { createContext, useState, useContext,useEffect } from "react";

const QuestionRecordContext = createContext();

function QuestionRecordProvider({ children }) {
 const [questionRecords, setQuestionRecords] = useState(() => {
    const saved = sessionStorage.getItem("questionRecords");
    return saved ? JSON.parse(saved) : [];
  });

  // ✅ Save to localStorage whenever questionRecords changes
  useEffect(() => {
    sessionStorage.setItem("questionRecords", JSON.stringify(questionRecords));
  }, [questionRecords]);

  // Helper function to validate question_id
  const isValidQuestionId = (id) => {
    return id !== undefined && id !== null && !isNaN(Number(id));
  };

  // Smart function that adds new record or updates existing one
  const addQuestionRecord = (newRecord) => {
    // Check if question_id is valid
    if (!isValidQuestionId(newRecord?.question_id)) {
      console.warn("Invalid question_id - record not saved");
      return;
    }

    setQuestionRecords((prevRecords) => {
      // Convert question_id to number
      const normalizedRecord = {
        ...newRecord,
        question_id: Number(newRecord.question_id),
        // Keep multiple_option_ids as is ([] or null)
        multiple_option_ids: newRecord.multiple_option_ids,
      };

      // Check if a record with this question_id already exists
      const existingRecordIndex = prevRecords.findIndex(
        (record) =>
          Number(record.question_id) === Number(normalizedRecord.question_id)
      );

      if (existingRecordIndex >= 0) {
        // If existing record is identical, return previous records as-is
        const existingRecord = prevRecords[existingRecordIndex];
        if (
          JSON.stringify(existingRecord) === JSON.stringify(normalizedRecord)
        ) {
          return prevRecords;
        }

        // Otherwise update existing record
        const updatedRecords = [...prevRecords];
        updatedRecords[existingRecordIndex] = {
          ...existingRecord,
          ...normalizedRecord, // Merge with new data
        };
        return updatedRecords;
      } else {
        return [...prevRecords, normalizedRecord];
      }
    });
  };

  // Get all question records
  const getQuestionRecords = () => {
    return questionRecords;
  };

  // Get a specific question record by question_id
  const getQuestionRecordById = (questionId) => {
    if (!isValidQuestionId(questionId)) return null;
    return questionRecords.find(
      (record) => Number(record.question_id) === Number(questionId)
    );
  };

  // Update a question record
  const updateQuestionRecord = (questionId, updatedData) => {
    if (!isValidQuestionId(questionId)) {
      console.warn("Invalid question_id - update failed");
      return;
    }

    setQuestionRecords((prevRecords) =>
      prevRecords.map((record) =>
        Number(record.question_id) === Number(questionId)
          ? {
              ...record,
              ...updatedData,
              question_id: Number(questionId),
              // Keep multiple_option_ids as is
              multiple_option_ids: updatedData.multiple_option_ids,
            }
          : record
      )
    );
  };

  // Delete a question record
  const deleteQuestionRecord = (questionId) => {
    if (!isValidQuestionId(questionId)) {
      console.warn("Invalid question_id - delete failed");
      return;
    }

    setQuestionRecords((prevRecords) =>
      prevRecords.filter(
        (record) => Number(record.question_id) !== Number(questionId)
      )
    );
  };

  // Clear all question records
  const clearQuestionRecords = () => {
    setQuestionRecords([]);
  };

  // Count summary by subject_id and section_id
  const getSectionSummary = (subjectId, sectionId, totalQuestion = 0) => {
    let sectionQuestions = [];

    if (sectionId) {
      sectionQuestions = questionRecords.filter(
        (q) =>
          Number(q.subject_id) === Number(subjectId) &&
          Number(q.section_id) === Number(sectionId)
      );
    } else {
      sectionQuestions = questionRecords.filter(
        (q) => Number(q.subject_id) === Number(subjectId)
      );
    }

    if (!sectionQuestions.length) {
      return {
        answered: 0,
        notAnswered: 0,
        markedWithAnswered: 0,
        marked: 0,
        notVisited: totalQuestion, // if no records, all are not visited
      };
    }

    const answered = sectionQuestions.reduce(
      (count, q) => (q.attempt_status === "attempted" ? count + 1 : count),
      0
    );

    const notAnswered = sectionQuestions.reduce(
      (count, q) => (q.attempt_status === "not answered" ? count + 1 : count),
      0
    );

    const markedWithAnswered = sectionQuestions.reduce(
      (count, q) =>
        q.attempt_status === "marked" &&
        (q.option_id || q.multiple_option_ids?.length || q.answer)
          ? count + 1
          : count,
      0
    );

    const marked = sectionQuestions.reduce(
      (count, q) =>
        q.attempt_status === "marked" &&
        !q.option_id &&
        !q.multiple_option_ids?.length &&
        !q.answer
          ? count + 1
          : count,
      0
    );

    const notVisited = Math.max(
      0,
      totalQuestion - (answered + notAnswered + markedWithAnswered + marked)
    );

    return {
      answered,
      notAnswered,
      markedWithAnswered,
      marked,
      notVisited,
    };
  };

  const hasAnyAttemptedOrMarked = () => {
    let total = 0;
    questionRecords.map((r) => {
      if (r.attempt_status === "attempted" || r.attempt_status === "marked") {
        total += 1;
      }
    });

    return total;
  };

  const value = {
    questionRecords,
    addQuestionRecord,
    getQuestionRecords,
    getQuestionRecordById,
    updateQuestionRecord,
    deleteQuestionRecord,
    clearQuestionRecords,
    getSectionSummary,
    hasAnyAttemptedOrMarked,
  };

  return (
    <QuestionRecordContext.Provider value={value}>
      {children}
    </QuestionRecordContext.Provider>
  );
}

const useQuestionRecord = () => {
  const context = useContext(QuestionRecordContext);
  if (context === undefined) {
    throw new Error(
      "useQuestionRecord must be used within a QuestionRecordProvider"
    );
  }
  return context;
};

export { useQuestionRecord };
export default QuestionRecordProvider;
